"use client";

import dynamic from "next/dynamic";
import type { ReactNode } from "react";
import type { SessionHour, SessionPlan, SessionPreferences } from "@/lib/session-planner/types";
import type { SupProfileSessionHour } from "@/lib/session-planner/api-types";
import type { SupProfileSessionPlan } from "@/lib/session-planner/plan";
import type { SessionContext } from "@/lib/session-planner/comfort";
import { daylightBand } from "@/lib/session-planner/explain";
import { SessionHeatmap } from "./SessionHeatmap";
import { NearbySpotSuggestion } from "./NearbySpotSuggestion";
import { BestWindowCard } from "./BestWindowCard";
import { SessionSupProfileBeta } from "./SessionPreferencesPanel";
import { prefScoringKey } from "@/lib/session-planner/preferences";
import { isSupProfileKey } from "@/lib/conditions/sport-profiles";

// MapLibre is lazy and client-only: it never enters the page shell, and only
// loads once the planner is expanded.
const SessionSpotMap = dynamic(
  () => import("./SessionSpotMap").then((m) => m.SessionSpotMap),
  {
    ssr: false,
    loading: () => <div className="h-[200px] w-full animate-pulse rounded-[14px] bg-warm-bg" />,
  }
);

type MapPoint = { lat: number; lng: number; name: string };

type Props = {
  plan: SessionPlan | SupProfileSessionPlan;
  prefs: SessionPreferences;
  ctx: SessionContext;
  hours: readonly SessionHour[] | readonly SupProfileSessionHour[];
  sportUrlKey: string;
  spot: MapPoint;
  nearbyLoc?: MapPoint | null;
  windowActions?: ReactNode;
};

const pad = (h: number) => `${String(h).padStart(2, "0")}:00`;

export function SessionTimeline({ plan, prefs, ctx, hours, sportUrlKey, spot, nearbyLoc, windowActions }: Props) {
  const hasAnyWindows = plan.windows.length > 0 || plan.bestWindow !== null;
  const todayDaylight = daylightBand(hours, 0);
  const fitEvidenceNote = "fitEvidenceNote" in plan ? plan.fitEvidenceNote : null;
  const scoringKey = prefScoringKey(prefs);
  const namedScoringKey = isSupProfileKey(scoringKey) ? scoringKey : undefined;

  return (
    <div className="space-y-5">
      {/* Best window hero card; otherwise the no-data message or a verdict banner
          (always renders something so a window-but-no-best state can't go silent). */}
      {plan.bestWindow ? (
        <BestWindowCard
          window={plan.bestWindow}
          prefs={prefs}
          ctx={ctx}
          waterComfort={plan.waterComfort}
          actions={windowActions}
        />
      ) : fitEvidenceNote ? (
        <PlannerUnavailableMessage
          fitEvidenceNote={fitEvidenceNote}
          scoringKey={namedScoringKey}
        />
      ) : (
        <PlannerNoWindowMessage
          allDayVerdict={plan.allDayVerdict}
          hasAnyWindows={hasAnyWindows}
          scoringKey={namedScoringKey}
        />
      )}

      <SessionHeatmap hours={hours} prefs={prefs} ctx={ctx} comprehension={plan.comprehension} />
      {todayDaylight ? <p className="text-[12px] text-text-muted">Daylight today {pad(todayDaylight.firstLightHour)}–{pad(todayDaylight.lastLightHour + 1)}</p> : null}

      {/* Nearby spot suggestion */}
      {plan.nearbySuggestion ? (
        <NearbySpotSuggestion suggestion={plan.nearbySuggestion} sportUrlKey={sportUrlKey} />
      ) : null}

      {/* The spot map */}
      <div>
        <h4 className="mb-2 font-serif text-[16px] font-bold text-navy">The spot</h4>
        <SessionSpotMap spot={spot} nearby={nearbyLoc ?? null} />
      </div>
    </div>
  );
}

export function PlannerUnavailableMessage({
  fitEvidenceNote,
  scoringKey,
}: {
  fitEvidenceNote: string;
  scoringKey?: string;
}) {
  return (
    <div className="rounded-[12px] border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900">
      {scoringKey ? (
        <div className="mb-1 font-semibold text-navy">
          <SessionSupProfileBeta scoringKey={scoringKey} surface="Planner" />
        </div>
      ) : null}
      <p>{fitEvidenceNote}. Headline quality remains visible above and is not altered by personal fit.</p>
    </div>
  );
}

export function PlannerNoWindowMessage({
  allDayVerdict,
  hasAnyWindows,
  scoringKey,
}: {
  allDayVerdict: string | null;
  hasAnyWindows: boolean;
  scoringKey?: string;
}) {
  if (allDayVerdict) {
    return (
      <div className="rounded-[12px] border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900">
        {scoringKey ? (
          <div className="mb-1 font-semibold text-navy">
            <SessionSupProfileBeta scoringKey={scoringKey} surface="Planner" />
          </div>
        ) : null}
        <p>{allDayVerdict}</p>
      </div>
    );
  }
  if (!hasAnyWindows) {
    return (
      <div className="text-[13px] text-text-muted">
        {scoringKey ? (
          <div className="mb-1 font-semibold text-navy">
            <SessionSupProfileBeta scoringKey={scoringKey} surface="Planner" />
          </div>
        ) : null}
        <p>Session timing is not available for this spot right now. See the daily summary above for
          the headline conditions.</p>
      </div>
    );
  }
  return (
    <div className="rounded-[12px] border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900">
      {scoringKey ? (
        <div className="mb-1 font-semibold text-navy">
          <SessionSupProfileBeta scoringKey={scoringKey} surface="Planner" />
        </div>
      ) : null}
      <p>No standout window in the next two days.</p>
    </div>
  );
}
